<template>
  <div v-bind:id="id" class="unlayer-web-editor" v-bind:style="{minHeight: minHeight}"></div>
</template>

<script>
import {loadScript} from './loadScript';
import {addPaddingsForParagraphs} from "./utils/addPaddingsForParagraphs";

export default {
  props: {
    editorId: String,
    options: Object,
    projectId: Number,
    tools: Object,
    tabs: Object,
    appearance: Object,
    displayMode: String,
    customCSS: Array,
    user: Object,
    mergeTags: Object,
    backgroundColor: String,
    fonts: Array,
    minHeight: {
      type: String,
      default: '700px',
    },
  },
  computed: {
    id() {
      return this.editorId || `unlayer-${Math.random().toString(36).slice(2)}`;
    },
    userId() {
      return parseInt(this.user.id);
    },
  },
  mounted() {
    loadScript(this.loadEditor.bind(this));
  },
  methods: {
    loadEditor() {
      const options = this.options || {};

      let default_fonts = [
        {
          label: "Andale Mono",
          value: "andale mono,times"
        },
        {
          label: "Arial",
          value: "arial,helvetica,sans-serif"
        },
        {
          label: "Arial Black",
          value: "arial black,avant garde,arial"
        },
        {
          label: "Book Antiqua",
          value: "book antiqua,palatino"
        },
        {
          label: "Comic Sans MS",
          value: "comic sans ms,sans-serif"
        },
        {
          label: "Courier New",
          value: "courier new,courier"
        },
        {
          label: "Georgia",
          value: "georgia,palatino"
        },
        {
          label: "Helvetica",
          value: "helvetica,sans-serif"
        },
        {
          label: "Impact",
          value: "impact,chicago"
        },
        {
          label: "Symbol",
          value: "symbol"
        },
        {
          label: "Tahoma",
          value: "tahoma,arial,helvetica,sans-serif"
        },
        {
          label: "Terminal",
          value: "terminal,monaco"
        },
        {
          label: "Times New Roman",
          value: "times new roman,times"
        },
        {
          label: "Trebuchet MS",
          value: "trebuchet ms,geneva"
        },
        {
          label: "Verdana",
          value: "verdana,geneva"
        },
        {
          label: "Lobster Two",
          value: "'Lobster Two',cursive",
          url: "https://fonts.googleapis.com/css?family=Lobster+Two:400,700&display=swap"
        },
        {
          label: "Playfair Display",
          value: "'Playfair Display',serif",
          url: "https://fonts.googleapis.com/css?family=Playfair+Display:400,700&display=swap"
        },
        {
          label: "Rubik",
          value: "'Rubik',sans-serif",
          url: "https://fonts.googleapis.com/css?family=Rubik:400,700&display=swap"
        },
        {
          label: "Source Sans Pro",
          value: "'Source Sans Pro',sans-serif",
          url: "https://fonts.googleapis.com/css?family=Source+Sans+Pro:400,700&display=swap"
        },
        {
          label: "Open Sans",
          value: "'Open Sans',sans-serif",
          url: "https://fonts.googleapis.com/css?family=Open+Sans:400,700&display=swap"
        },
        {
          label: "Crimson Text",
          value: "'Crimson Text',serif",
          url: "https://fonts.googleapis.com/css?family=Crimson+Text:400,700&display=swap"
        },
        {
          label: "Montserrat",
          value: "'Montserrat',sans-serif",
          url: "https://fonts.googleapis.com/css?family=Montserrat:400,700&display=swap"
        },
        {
          label: "Old Standard TT",
          value: "'Old Standard TT',serif",
          url: "https://fonts.googleapis.com/css?family=Old+Standard+TT:400,700&display=swap"
        },
        {
          label: "Lato",
          value: "'Lato',sans-serif",
          url: "https://fonts.googleapis.com/css?family=Lato:400,700&display=swap"
        },
        {
          label: "Raleway",
          value: "'Raleway',sans-serif",
          url: "https://fonts.googleapis.com/css?family=Raleway:400,700&display=swap"
        },
        {
          label: "Cabin",
          value: "'Cabin',sans-serif",
          url: "https://fonts.googleapis.com/css?family=Cabin:400,700&display=swap"
        },
        {
          label: "Pacifico",
          value: "'Pacifico',cursive",
          url: "https://fonts.googleapis.com/css?family=Pacifico&display=swap"
        },
        {
          label: "Inter",
          value: "'Inter',sans-serif",
          url: "https://fonts.googleapis.com/css?family=Inter&display=swap"
        },
        {
          label: "Inter",
          value: "'Inter',sans-serif",
          url: "https://fonts.googleapis.com/css?family=Inter:100,200,300,regular,500,600,700,800,900&display=swap"
        },
        {
          label: "Abel",
          value: "'Abel',sans-serif",
          url: "https://fonts.googleapis.com/css?family=Abel:regular&display=swap"
        }
      ];

      let imported_fonts = JSON.parse(JSON.stringify(this.fonts));
      // remove any duplicates
      let filtered_fonts = default_fonts.filter(font => ! imported_fonts.find((imported_font) => imported_font.label === font.label))
      // add imported fonts
      filtered_fonts.push(...imported_fonts);

      // set default fonts
      options.fonts = {
        showDefaultFonts: false,
        customFonts: filtered_fonts,
      };

      for (let property in this.$options.propsData) {
        if (property === 'fonts') {
          continue;
        }
        if (this[property]) {
          options[property] = this[property];
        }
      }

      options.version = '1.5.75';

      this.editor = unlayer.createEditor({
        ...options,
        id: this.id,
        user: {
          id: this.userId,
        },
        editor: {
          autoSelectOnDrop: true,
        },
      });

      this.$emit('load');

      this.editor.addEventListener('design:updated', data => {
        this.editor?.exportHtml(ctx => {
          this.$emit('update:design', ctx.design, data);
        });
      });

      this.editor?.setBodyValues({
        backgroundColor: this.backgroundColor,
      });

      this.editor.addEventListener('editor:ready', () => {

        this.$emit('ready');
      });

      this.editor.registerCallback('previewHtml', (params, done) => {
        done({
          html: this.displayMode === 'email' ? addPaddingsForParagraphs(params.html) : params.html
        });
      });
    },
    loadDesign(design) {
      this.editor.loadDesign(design);
    },
    saveDesign(callback) {
      this.editor.saveDesign(callback);
    },
    exportHtml(callback) {
      this.editor.exportHtml(callback);
    },
  },
};
</script>

<style scoped>
.unlayer-web-editor {
  flex: 1;
  display: flex;
}
</style>
